import type { MainToWorker, WorkerToMain } from './protocol';

export const SIDELOAD_MAX_BYTES = 512 * 1024;
export const SIDELOAD_CHUNK_BYTES = 32;
export const SIDELOAD_COMMAND_DELAY_MS = 250;
export const SIDELOAD_CONNECT_SETTLE_MS = 2_000;
export const SIDELOAD_RECEIVE_SETTLE_MS = 750;
export const SIDELOAD_PACKET_RECEIVE_SETTLE_MS = 0;
export const SIDELOAD_PACKET_PAYLOAD_BYTES = 512;
export const SIDELOAD_PACKET_CHUNK_DELAY_MS = 350;
export const SIDELOAD_PROMPT_TIMEOUT_MS = 8_000;
export const SIDELOAD_PACED_BYTE_DELAY_MS = 125;
export const SIDELOAD_CHUNK_DELAY_MS = 700;
export const SIDELOAD_REFRESH_DELAY_MS = 1_500;
export const SIDELOAD_CONSOLE_TX_CAP_BYTES = 64 * 1024;

export type ConsoleSideloadProtocol = 'cyberload' | 'packet' | 'raw';

interface SideloadFileInfo {
  readonly name: string;
  readonly size: number;
}

export interface ConsoleSideloadOptions {
  readonly protocol?: ConsoleSideloadProtocol;
  readonly chunkBytes?: number;
  readonly chunkDelayMs?: number;
  readonly commandDelayMs?: number;
  readonly idleCommand?: string | null;
  readonly connectCommand?: string | null;
  readonly connectSettleMs?: number;
  readonly receiveSettleMs?: number;
  readonly packetPayloadBytes?: number;
  readonly promptTimeoutMs?: number;
  readonly refreshDelayMs?: number;
}

export interface ConsoleSideloadPlan {
  readonly filename: string;
  readonly size: number;
  readonly idleCommand: string;
  readonly connectCommand: string;
  readonly receiveCommand: string;
  readonly refreshCommand: string;
  readonly chunkCount: number;
}

export interface ConsoleSideloadResult {
  readonly filename: string;
  readonly size: number;
  readonly protocol: ConsoleSideloadProtocol;
  readonly chunkCount: number;
  readonly chunkBytes: number;
  readonly chunkDelayMs: number;
  readonly connectSettleMs: number;
  readonly receiveSettleMs: number;
  readonly packetPayloadBytes: number;
  readonly packetCount: number;
  readonly promptTimeoutMs: number;
  readonly promptsSeen: readonly string[];
  readonly refreshDelayMs: number;
}

interface CybikoSideloadDebug {
  readonly sendFile: (
    file: File,
    refreshRoot?: boolean,
    options?: ConsoleSideloadOptions,
  ) => Promise<ConsoleSideloadResult>;
  readonly sendConsoleText: (text: string) => Promise<void>;
  readonly sendConsoleCommand: (command: string) => Promise<void>;
  readonly sendConsoleTextPaced: (text: string, byteDelayMs?: number) => Promise<void>;
  readonly sendConsoleTextAcked: (
    text: string,
    ackTimeoutMs?: number,
  ) => Promise<{ readonly ackCount: number; readonly timeoutCount: number }>;
  readonly planFile: (file: SideloadFileInfo) => ConsoleSideloadPlan;
  readonly validateFile: (file: SideloadFileInfo) => string | null;
  readonly getStatus: () => string;
  readonly getConsoleTxText: () => string;
  readonly recordConsoleTxText: (text: string) => void;
  readonly dumpFlash: () => Promise<Uint8Array>;
}

declare global {
  interface Window {
    __cybikoSideloadDebug?: CybikoSideloadDebug;
  }
}

export function normalizeAppFilename(name: string): string | null {
  const trimmed = name.trim();
  if (!trimmed || /[\\/:]/.test(trimmed)) return null;
  const lowered = trimmed.toLowerCase();
  return /^[a-z0-9_-]{1,8}\.app$/.test(lowered) ? lowered : null;
}

export function validateSideloadFile(file: SideloadFileInfo): string | null {
  if (!normalizeAppFilename(file.name)) {
    return 'Use an 8.3 .app filename, for example cyhello.app.';
  }
  if (!Number.isFinite(file.size) || file.size <= 0) {
    return 'Selected .app is empty.';
  }
  if (file.size > SIDELOAD_MAX_BYTES) {
    return `Selected .app is larger than ${formatBytes(SIDELOAD_MAX_BYTES)}.`;
  }
  return null;
}

export function planConsoleSideload(file: SideloadFileInfo): ConsoleSideloadPlan {
  const error = validateSideloadFile(file);
  if (error) throw new Error(error);
  const filename = normalizeAppFilename(file.name);
  if (!filename) throw new Error('Invalid .app filename.');
  return {
    filename,
    size: file.size,
    idleCommand: '\r\n',
    connectCommand: 'pc on\r\n',
    receiveCommand: `rcv ${filename} ${file.size}\r\n`,
    refreshCommand: 'pc root\r\n',
    chunkCount: Math.ceil(file.size / SIDELOAD_CHUNK_BYTES),
  };
}

let consoleTxBaseOffset = 0;
let consoleTxText = '';

export function recordConsoleSideloadTxByte(byte: number): void {
  consoleTxText += String.fromCharCode(byte & 0xff);
  if (consoleTxText.length > SIDELOAD_CONSOLE_TX_CAP_BYTES) {
    const dropBytes = consoleTxText.length - SIDELOAD_CONSOLE_TX_CAP_BYTES;
    consoleTxText = consoleTxText.slice(dropBytes);
    consoleTxBaseOffset += dropBytes;
  }
}

export function recordConsoleSideloadTxText(text: string): void {
  for (let i = 0; i < text.length; i++) {
    recordConsoleSideloadTxByte(text.charCodeAt(i));
  }
}

export function consoleSideloadTxText(): string {
  return consoleTxText;
}

export function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) {
    crc ^= byte;
    for (let i = 0; i < 8; i++) {
      const mask = -(crc & 1);
      crc = (crc >>> 1) ^ (0xedb88320 & mask);
    }
  }
  return (~crc) >>> 0;
}

export function cybikoConsoleCommandChecksum(command: string): number {
  let checksum = 0;
  const bytes = asciiBytes(command);
  for (const byte of bytes) {
    checksum = (((checksum ^ byte) << 1) | ((checksum >>> 16) & 1)) >>> 0;
  }
  return checksum & 0xffff;
}

export function encodeCybikoConsoleCommand(command: string): string {
  const normalized = command.replace(/[\r\n]+$/g, '');
  const checksum = cybikoConsoleCommandChecksum(normalized);
  return `${normalized}\r\n${String.fromCharCode(checksum & 0xff)}${String.fromCharCode((checksum >>> 8) & 0xff)}`;
}

export function buildCybikoRcvPacket(packetIndex: number, payload: Uint8Array): Uint8Array {
  if (!Number.isInteger(packetIndex) || packetIndex < 0 || packetIndex > 0xffff) {
    throw new Error('Cybiko rcv packet index must fit in uint16.');
  }
  if (payload.byteLength > SIDELOAD_PACKET_PAYLOAD_BYTES) {
    throw new Error(`Cybiko rcv packets cannot exceed ${SIDELOAD_PACKET_PAYLOAD_BYTES} bytes.`);
  }

  const crcInput = new Uint8Array(4 + payload.byteLength);
  crcInput[0] = packetIndex & 0xff;
  crcInput[1] = (packetIndex >>> 8) & 0xff;
  crcInput[2] = payload.byteLength & 0xff;
  crcInput[3] = (payload.byteLength >>> 8) & 0xff;
  crcInput.set(payload, 4);

  const packetCrc = crc32(crcInput);
  const packet = new Uint8Array(1 + crcInput.byteLength + 4);
  packet[0] = 0x43; // 'C': CyOS packet-transfer start byte.
  packet.set(crcInput, 1);
  packet[packet.byteLength - 4] = packetCrc & 0xff;
  packet[packet.byteLength - 3] = (packetCrc >>> 8) & 0xff;
  packet[packet.byteLength - 2] = (packetCrc >>> 16) & 0xff;
  packet[packet.byteLength - 1] = (packetCrc >>> 24) & 0xff;
  return packet;
}

export function initConsoleSideload(worker: Worker | undefined): void {
  const params = new URLSearchParams(window.location.search);
  if (params.get('sideload') !== '1') return;

  document.body.classList.add('sideload-mode');

  const panel = document.createElement('section');
  panel.className = 'sideload-panel';
  panel.setAttribute('aria-label', 'Debug console sideload');

  const title = document.createElement('div');
  title.className = 'sideload-title';
  title.textContent = 'console sideload';

  const input = document.createElement('input');
  input.className = 'sideload-input';
  input.type = 'file';
  input.accept = '.app,application/octet-stream';

  const sendButton = document.createElement('button');
  sendButton.className = 'sideload-send';
  sendButton.type = 'button';
  sendButton.textContent = 'send .app';

  const status = document.createElement('output');
  status.className = 'sideload-status';
  status.textContent = worker
    ? 'Boot to Ready >, choose a local .app, then send.'
    : 'Console sideload is unavailable without the emulator worker.';

  if (!worker) {
    input.disabled = true;
    sendButton.disabled = true;
  }

  panel.append(title, input, sendButton, status);
  document.body.append(panel);

  const setStatus = (text: string): void => {
    status.textContent = text;
  };

  const sendFile = async (
    file: File,
    refreshRoot = true,
    options: ConsoleSideloadOptions = {},
  ): Promise<ConsoleSideloadResult> => {
    if (!worker) throw new Error('Console sideload is unavailable without the emulator worker.');

    const plan = planConsoleSideload(file);
    const protocol = normalizeProtocol(options.protocol);
    const commandDelayMs = normalizedDelay(options.commandDelayMs, SIDELOAD_COMMAND_DELAY_MS);
    const connectCommand = options.connectCommand === undefined
      ? protocol === 'cyberload'
        ? null
        : plan.connectCommand
      : options.connectCommand;
    const connectSettleMs = normalizedDelay(options.connectSettleMs, SIDELOAD_CONNECT_SETTLE_MS);
    const receiveSettleMs = normalizedDelay(
      options.receiveSettleMs,
      protocol === 'packet' ? SIDELOAD_PACKET_RECEIVE_SETTLE_MS : SIDELOAD_RECEIVE_SETTLE_MS,
    );
    const refreshDelayMs = normalizedDelay(options.refreshDelayMs, SIDELOAD_REFRESH_DELAY_MS);
    const chunkBytes = normalizedPositiveInteger(options.chunkBytes, SIDELOAD_CHUNK_BYTES);
    const chunkDelayMs = normalizedDelay(
      options.chunkDelayMs,
      protocol === 'packet' ? SIDELOAD_PACKET_CHUNK_DELAY_MS : SIDELOAD_CHUNK_DELAY_MS,
    );
    const packetPayloadBytes = normalizedBoundedInteger(
      options.packetPayloadBytes,
      SIDELOAD_PACKET_PAYLOAD_BYTES,
      1,
      SIDELOAD_PACKET_PAYLOAD_BYTES,
    );
    const promptTimeoutMs = normalizedDelay(options.promptTimeoutMs, SIDELOAD_PROMPT_TIMEOUT_MS);
    const idleCommand = options.idleCommand === undefined
      ? protocol === 'cyberload'
        ? null
        : plan.idleCommand
      : options.idleCommand;
    if (idleCommand) {
      postToWorker(worker, { type: 'console-rx', text: idleCommand });
      await delay(commandDelayMs);
    }
    if (connectCommand) {
      setStatus('entering PC connection mode');
      postToWorker(worker, { type: 'console-rx', text: connectCommand });
      await delay(connectSettleMs);
    } else {
      setStatus('skipping PC connection mode');
    }
    const receiveCommand = protocol === 'cyberload'
      ? encodeCybikoConsoleCommand(plan.receiveCommand)
      : plan.receiveCommand;
    setStatus(`sending ${plan.receiveCommand.trim()}`);
    const promptCursor = currentConsoleTxCursor();
    postToWorker(worker, { type: 'console-rx', text: receiveCommand });
    await delay(receiveSettleMs);

    const bytes = new Uint8Array(await file.arrayBuffer());
    const transfer = protocol === 'packet' || protocol === 'cyberload'
      ? await sendPacketizedFile(worker, bytes, {
        packetPayloadBytes,
        chunkBytes,
        chunkDelayMs,
        promptCursor,
        promptTimeoutMs,
        acceptInitialControlAck: protocol === 'packet',
        setStatus,
      })
      : await sendRawFile(worker, bytes, { chunkBytes, chunkDelayMs, setStatus });

    if (refreshRoot) {
      await delay(refreshDelayMs);
      const refreshCommand = protocol === 'cyberload'
        ? encodeCybikoConsoleCommand(plan.refreshCommand)
        : plan.refreshCommand;
      postToWorker(worker, { type: 'console-rx', text: refreshCommand });
      setStatus(`sent pc root for ${plan.filename}`);
    } else {
      setStatus(`uploaded ${plan.filename}`);
    }

    return {
      filename: plan.filename,
      size: plan.size,
      protocol,
      chunkCount: Math.ceil(plan.size / chunkBytes),
      chunkBytes,
      chunkDelayMs,
      connectSettleMs,
      receiveSettleMs,
      packetPayloadBytes,
      packetCount: transfer.packetCount,
      promptTimeoutMs,
      promptsSeen: transfer.promptsSeen,
      refreshDelayMs,
    };
  };

  const sendConsoleText = async (text: string): Promise<void> => {
    if (!worker) throw new Error('Console sideload is unavailable without the emulator worker.');
    postToWorker(worker, { type: 'console-rx', text });
    setStatus(`sent ${text.trim()}`);
    await delay(SIDELOAD_COMMAND_DELAY_MS);
  };

  const sendConsoleCommand = async (command: string): Promise<void> => {
    if (!worker) throw new Error('Console sideload is unavailable without the emulator worker.');
    const encoded = encodeCybikoConsoleCommand(command);
    postToWorker(worker, { type: 'console-rx', text: encoded });
    setStatus(`sent command ${command.trim()}`);
    await delay(SIDELOAD_COMMAND_DELAY_MS);
  };

  const sendConsoleTextPaced = async (
    text: string,
    byteDelayMs = SIDELOAD_PACED_BYTE_DELAY_MS,
  ): Promise<void> => {
    if (!worker) throw new Error('Console sideload is unavailable without the emulator worker.');
    const normalizedDelay = Math.max(0, Math.floor(byteDelayMs));
    for (let i = 0; i < text.length; i++) {
      postToWorker(worker, { type: 'console-rx', text: text[i] ?? '' });
      if (normalizedDelay > 0) await delay(normalizedDelay);
    }
    setStatus(`sent paced ${text.trim()}`);
    await delay(SIDELOAD_COMMAND_DELAY_MS);
  };

  const sendConsoleTextAcked = async (
    text: string,
    ackTimeoutMs = SIDELOAD_PROMPT_TIMEOUT_MS,
  ): Promise<{ readonly ackCount: number; readonly timeoutCount: number }> => {
    if (!worker) throw new Error('Console sideload is unavailable without the emulator worker.');
    let ackCount = 0;
    let timeoutCount = 0;
    for (let i = 0; i < text.length; i++) {
      const cursor = currentConsoleTxCursor();
      postToWorker(worker, { type: 'console-rx', text: text[i] ?? '' });
      const acked = await waitForControlAck(cursor, ackTimeoutMs);
      if (acked) ackCount++;
      else timeoutCount++;
    }
    setStatus(`sent acked ${text.trim()} (${ackCount} ack, ${timeoutCount} timeout)`);
    await delay(SIDELOAD_COMMAND_DELAY_MS);
    return { ackCount, timeoutCount };
  };

  const dumpFlash = async (): Promise<Uint8Array> => {
    if (!worker) throw new Error('Console sideload is unavailable without the emulator worker.');
    const requestId = `flash-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    return new Promise<Uint8Array>((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        worker.removeEventListener('message', onMessage);
        reject(new Error('Timed out waiting for flash dump.'));
      }, 8_000);
      const onMessage = (event: MessageEvent<WorkerToMain>): void => {
        const msg = event.data;
        if (!msg || typeof msg !== 'object') return;
        if (
          (msg.type === 'flash-dump' || msg.type === 'flash-dump-error') &&
          msg.requestId === requestId
        ) {
          window.clearTimeout(timeout);
          worker.removeEventListener('message', onMessage);
          if (msg.type === 'flash-dump') resolve(new Uint8Array(msg.buffer));
          else reject(new Error(msg.message));
        }
      };
      worker.addEventListener('message', onMessage);
      postToWorker(worker, { type: 'dump-flash', requestId });
    });
  };

  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (!file) {
      setStatus('Choose a local .app file.');
      return;
    }
    const error = validateSideloadFile(file);
    setStatus(error ?? `ready: ${normalizeAppFilename(file.name)} (${formatBytes(file.size)})`);
  });

  sendButton.addEventListener('click', () => {
    const file = input.files?.[0];
    if (!file) {
      setStatus('Choose a local .app file.');
      return;
    }
    const error = validateSideloadFile(file);
    if (error) {
      setStatus(error);
      return;
    }
    void sendFile(file).catch((err: unknown) => {
      setStatus(err instanceof Error ? err.message : String(err));
    });
  });

  window.__cybikoSideloadDebug = {
    sendFile,
    sendConsoleText,
    sendConsoleCommand,
    sendConsoleTextPaced,
    sendConsoleTextAcked,
    planFile: planConsoleSideload,
    validateFile: validateSideloadFile,
    getStatus: () => status.textContent ?? '',
    getConsoleTxText: consoleSideloadTxText,
    recordConsoleTxText: recordConsoleSideloadTxText,
    dumpFlash,
  };
}

interface TransferStatus {
  readonly setStatus: (text: string) => void;
}

interface RawTransferOptions extends TransferStatus {
  readonly chunkBytes: number;
  readonly chunkDelayMs: number;
}

interface PacketTransferOptions extends RawTransferOptions {
  readonly packetPayloadBytes: number;
  readonly promptCursor: number;
  readonly promptTimeoutMs: number;
  readonly acceptInitialControlAck: boolean;
}

interface TransferResult {
  readonly packetCount: number;
  readonly promptsSeen: readonly string[];
}

async function sendRawFile(
  worker: Worker,
  bytes: Uint8Array,
  options: RawTransferOptions,
): Promise<TransferResult> {
  await sendBytes(worker, bytes, options.chunkBytes, options.chunkDelayMs, (sent) => {
    options.setStatus(`streamed ${sent} / ${bytes.byteLength} bytes`);
  });
  return { packetCount: 0, promptsSeen: [] };
}

async function sendPacketizedFile(
  worker: Worker,
  bytes: Uint8Array,
  options: PacketTransferOptions,
): Promise<TransferResult> {
  const packetCount = Math.ceil(bytes.byteLength / options.packetPayloadBytes);
  const promptsSeen: string[] = [];
  let promptCursor = options.promptCursor;
  let packetIndex = 0;
  let resendCount = 0;

  const commandResponse = await waitForTransferResponse(promptCursor, options.promptTimeoutMs, {
    acceptControlAck: options.acceptInitialControlAck,
  });
  promptsSeen.push(commandResponse.label);
  if (commandResponse.kind === 'cancel' || commandResponse.kind === 'nack') {
    throw new Error(`CyOS rejected rcv command with ${commandResponse.label}.`);
  }
  promptCursor = commandResponse.cursor;

  while (packetIndex < packetCount) {
    const expectedPacket = packetIndex;

    const packetOffset = expectedPacket * options.packetPayloadBytes;
    const payload = bytes.slice(packetOffset, packetOffset + options.packetPayloadBytes);
    const packet = buildCybikoRcvPacket(expectedPacket, payload);
    const sentFileBytes = Math.min(packetOffset + payload.byteLength, bytes.byteLength);
    promptCursor = currentConsoleTxCursor();
    options.setStatus(`sending packet ${expectedPacket + 1} / ${packetCount}`);
    await sendBytes(worker, packet, options.chunkBytes, options.chunkDelayMs, () => {
      options.setStatus(`streamed ${sentFileBytes} / ${bytes.byteLength} bytes`);
    });

    const response = await waitForTransferResponse(promptCursor, options.promptTimeoutMs, {
      acceptControlAck: true,
    });
    promptsSeen.push(response.label);
    promptCursor = response.cursor;

    if (response.kind === 'done') break;
    if (response.kind === 'cancel') {
      throw new Error(`CyOS cancelled packetized rcv transfer with ${response.label}.`);
    }
    if (response.kind === 'send') {
      packetIndex = Math.max(0, Math.min(response.packetIndex, packetCount - 1));
      resendCount++;
    } else if (response.kind === 'nack') {
      resendCount++;
    } else {
      packetIndex = expectedPacket + 1;
      resendCount = 0;
    }
    if (resendCount > packetCount + 8) {
      throw new Error('CyOS requested too many packet retransmits during rcv transfer.');
    }
  }

  return { packetCount, promptsSeen };
}

type TransferResponse =
  | { readonly kind: 'ack'; readonly label: string; readonly cursor: number }
  | { readonly kind: 'done'; readonly label: string; readonly cursor: number }
  | { readonly kind: 'nack'; readonly label: string; readonly cursor: number }
  | { readonly kind: 'cancel'; readonly label: string; readonly cursor: number }
  | {
    readonly kind: 'send';
    readonly label: string;
    readonly cursor: number;
    readonly packetIndex: number;
  };

async function waitForTransferResponse(
  startCursor: number,
  timeoutMs: number,
  options: { readonly acceptControlAck: boolean },
): Promise<TransferResponse> {
  const deadline = performance.now() + timeoutMs;
  while (performance.now() < deadline) {
    const text = consoleTxSince(startCursor);
    const parsed = parseTransferResponse(text, startCursor, options.acceptControlAck);
    if (parsed) return parsed;
    await delay(50);
  }
  throw new Error('Timed out waiting for CyOS packet-transfer response.');
}

async function waitForControlAck(startCursor: number, timeoutMs: number): Promise<boolean> {
  const deadline = performance.now() + Math.max(0, timeoutMs);
  while (performance.now() < deadline) {
    if (consoleTxSince(startCursor).includes('\x01')) return true;
    await delay(20);
  }
  return false;
}

function parseTransferResponse(
  text: string,
  startCursor: number,
  acceptControlAck: boolean,
): TransferResponse | null {
  const lower = text.toLowerCase();
  const cancelIndex = firstIndexOf(lower, ['cancel', 'can']);
  if (cancelIndex >= 0) {
    return { kind: 'cancel', label: lower.slice(cancelIndex, cancelIndex + 6), cursor: startCursor + text.length };
  }
  const doneIndex = lower.indexOf('done');
  if (doneIndex >= 0) {
    return { kind: 'done', label: 'done', cursor: startCursor + text.length };
  }
  const nackIndex = lower.indexOf('nack');
  if (nackIndex >= 0) {
    return { kind: 'nack', label: 'nack', cursor: startCursor + text.length };
  }
  const sendMatch = lower.match(/send\s*(\d+)/);
  if (sendMatch?.index !== undefined) {
    return {
      kind: 'send',
      label: sendMatch[0],
      cursor: startCursor + text.length,
      packetIndex: Number(sendMatch[1]),
    };
  }
  const ackIndex = lower.indexOf('ack');
  if (ackIndex >= 0) {
    return { kind: 'ack', label: 'ack', cursor: startCursor + text.length };
  }
  if (acceptControlAck) {
    const controlAck = firstIndexOf(text, ['\x01', '\x02']);
    if (controlAck >= 0) {
      const byte = text.charCodeAt(controlAck);
      return {
        kind: 'ack',
        label: `control-0x${byte.toString(16).padStart(2, '0')}`,
        cursor: startCursor + controlAck + 1,
      };
    }
  }
  return null;
}

function firstIndexOf(text: string, values: readonly string[]): number {
  let result = -1;
  for (const value of values) {
    const index = text.indexOf(value);
    if (index >= 0 && (result < 0 || index < result)) result = index;
  }
  return result;
}

async function sendBytes(
  worker: Worker,
  bytes: Uint8Array,
  chunkBytes: number,
  chunkDelayMs: number,
  onChunkSent: (sent: number) => void,
): Promise<void> {
  for (let offset = 0; offset < bytes.byteLength; offset += chunkBytes) {
    const chunk = bytes.slice(offset, offset + chunkBytes);
    const msg: MainToWorker = { type: 'console-rx-bytes', bytes: chunk.buffer };
    worker.postMessage(msg, [chunk.buffer]);
    const sent = Math.min(offset + chunkBytes, bytes.byteLength);
    onChunkSent(sent);
    if (chunkDelayMs > 0) await delay(chunkDelayMs);
  }
}

function currentConsoleTxCursor(): number {
  return consoleTxBaseOffset + consoleTxText.length;
}

function consoleTxSince(cursor: number): string {
  const localCursor = Math.max(0, cursor - consoleTxBaseOffset);
  return consoleTxText.slice(localCursor);
}

function postToWorker(worker: Worker, msg: MainToWorker): void {
  worker.postMessage(msg);
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function normalizedDelay(value: number | undefined, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(0, Math.floor(Number(value)));
}

function normalizedPositiveInteger(value: number | undefined, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.max(1, Math.floor(Number(value)));
}

function normalizeProtocol(value: ConsoleSideloadProtocol | undefined): ConsoleSideloadProtocol {
  if (value === 'packet' || value === 'raw') return value;
  return 'cyberload';
}

function normalizedBoundedInteger(
  value: number | undefined,
  fallback: number,
  min: number,
  max: number,
): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.floor(Number(value))));
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kib = bytes / 1024;
  return `${Math.round(kib)} KiB`;
}

function asciiBytes(text: string): Uint8Array {
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    if (code > 0x7f) {
      throw new Error('Cybiko console commands must be ASCII.');
    }
    bytes[i] = code;
  }
  return bytes;
}
